import {
  collection,
  doc,
  documentId,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";
import { db } from "./firebase";
import { normalizeLegacyIdentity } from "./identity";
import type {
  AccountType,
  IdentityEntitlements,
  LegacyAccountType,
  PublicProfile,
  UserProfile,
  VerificationStatus,
} from "../types/models";

type PublicProfileInput = Partial<
  Pick<
    PublicProfile,
    | "username"
    | "displayName"
    | "institutionName"
    | "role"
    | "accountType"
    | "verificationStatus"
    | "entitlements"
    | "teacherVerified"
    | "photoURL"
    | "createdAt"
    | "updatedAt"
  >
> & {
  role?: LegacyAccountType | "";
  accountType?: AccountType | "";
  verificationStatus?: VerificationStatus;
  entitlements?: IdentityEntitlements;
  track?: PublicProfile["track"] | "";
  mainSubject?: PublicProfile["mainSubject"] | "";
};

function stringOrEmpty(value: unknown) {
  return typeof value === "string" ? value : "";
}

function nonNegativeIntOrZero(value: unknown) {
  return Number.isInteger(value) && Number(value) >= 0 ? Number(value) : 0;
}

function chunk<T>(arr: T[], size: number) {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

export type PublicProfileRecord = PublicProfile & {
  id: string;
};

export function mapPublicProfile(id: string, data: any): PublicProfileRecord {
  const identity = normalizeLegacyIdentity(data);
  const accountType = identity.accountType;
  const legacyRole = accountType === "student" || accountType === "teacher"
    ? accountType
    : undefined;

  return {
    id,
    uid: typeof data?.uid === "string" && data.uid ? data.uid : id,
    username: stringOrEmpty(data?.username),
    displayName: stringOrEmpty(data?.displayName),
    ...(stringOrEmpty(data?.institutionName) ? { institutionName: stringOrEmpty(data?.institutionName) } : {}),
    ...(accountType ? { accountType } : {}),
    ...(legacyRole ? { role: legacyRole } : {}),
    verificationStatus: identity.verificationStatus,
    teacherVerified: accountType === "teacher" && identity.verificationStatus === "verified",
    track: accountType === "student" ? data?.track : "",
    mainSubject: accountType === "teacher" ? data?.mainSubject : "",
    ...(identity.entitlements ? { entitlements: identity.entitlements } : {}),
    ...(Array.isArray(data?.badges)
      ? {
        badges: data.badges.filter(
          (badge: any) => badge && typeof badge.kind === "string" && typeof badge.label === "string"
        ),
      }
      : {}),
    photoURL: stringOrEmpty(data?.photoURL),
    followersCount: nonNegativeIntOrZero(data?.followersCount),
    followingCount: nonNegativeIntOrZero(data?.followingCount),
    createdAt: data?.createdAt,
    updatedAt: data?.updatedAt,
  };
}

/** Map the C3 account projection into the existing public-profile read model. */
export function mapPublicAccount(id: string, data: any): PublicProfileRecord {
  return {
    ...mapPublicProfile(id, {
      ...data,
      uid: typeof data?.legacyUid === "string" && data.legacyUid ? data.legacyUid : id,
    }),
    identityId: id,
  };
}

export function buildPublicProfileData(
  uid: string,
  profile: PublicProfileInput | Partial<UserProfile>,
  options: { includeCreatedAt?: boolean } = {}
) {
  const identity = normalizeLegacyIdentity(profile);
  const accountType = identity.accountType;
  const legacyRole = accountType === "student" || accountType === "teacher"
    ? accountType
    : null;

  const data: Record<string, unknown> = {
    uid,
    username: stringOrEmpty(profile.username),
    displayName: stringOrEmpty(profile.displayName),
    ...(accountType ? { accountType } : {}),
    ...(legacyRole ? { role: legacyRole } : {}),
    verificationStatus: identity.verificationStatus,
    teacherVerified: accountType === "teacher" && identity.verificationStatus === "verified",
    track: accountType === "student" ? stringOrEmpty(profile.track) : "",
    mainSubject: accountType === "teacher" ? stringOrEmpty(profile.mainSubject) : "",
    photoURL: stringOrEmpty(profile.photoURL),
    updatedAt: serverTimestamp(),
  };

  if (identity.entitlements) data.entitlements = identity.entitlements;

  if (options.includeCreatedAt) {
    data.createdAt = profile.createdAt ?? serverTimestamp();
  }

  return data;
}

export async function upsertPublicProfile(
  uid: string,
  profile: PublicProfileInput | Partial<UserProfile>,
  options: { includeCreatedAt?: boolean } = {}
) {
  await setDoc(
    doc(db, "publicProfiles", uid),
    buildPublicProfileData(uid, profile, options),
    { merge: true }
  );
}

export async function getPublicProfile(uid: string) {
  const snap = await getDoc(doc(db, "publicProfiles", uid));
  return snap.exists() ? mapPublicProfile(snap.id, snap.data()) : null;
}

export async function getPublicProfilesByIds(uids: string[]) {
  const uniqueUids = Array.from(new Set(uids.filter(Boolean)));
  const profilesById: Record<string, PublicProfileRecord> = {};

  const snapshots = await Promise.all(
    chunk(uniqueUids, 10).map((part) =>
      getDocs(query(collection(db, "publicProfiles"), where(documentId(), "in", part)))
    )
  );

  for (const snap of snapshots) {
    snap.forEach((item) => {
      profilesById[item.id] = mapPublicProfile(item.id, item.data());
    });
  }

  return profilesById;
}
